import {
  Alert,
  Backdrop,
  Box,
  Stack,
  useMediaQuery,
} from '@ace-clublabs/shared-component-library';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';

import ErrorPage from '@/components/shared/ErrorPage';
import LoadingContainer from '@/components/shared/LoadingContainer';
import MobileWizard from '@/components/shared/MobileWizard';
import SplitPageLayout from '@/components/shared/SplitPageLayout';
import type { Branch } from '@/features/appointments/utils/Branch';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import { getClubCode, getZipCode, Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import { ErrorFallback } from '@/providers/app';
import useAppointmentStore, {
  isInBranchAppointment as isInBranchAppointmentSelected,
  isOnlineAppointment as isOnlineAppointmentSelected,
} from '@/store/AppointmentStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import useSessionStore, { type FlowType } from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import { useAgentsAndTimeslotsQuery } from '../api/getAppointmentSlots';
import { useBranches } from '../api/getBranches';
import { useValidateSelectedTime } from '../api/useValidateSelectedTime';
import AgentSelectionModule from '../components/modules/AgentSelectionModule';
import AppointmentTypeModule from '../components/modules/AppointmentTypeModule';
import BranchSelectionModule from '../components/modules/BranchSelectionModule';
import DateSelectionModule from '../components/modules/DateSelectionModule';
import NoAdvisorsAvailableModal from '../components/NoAdvisorsAvailableModal';
import NoAgentsFound from '../components/NoAgentsFound';
import type { AgentInfo } from '../utils/AgentInfo';
import { shuffleArray } from '../utils/shuffleArray';
import type { TimeSlot } from '../utils/TimeSlot';

const ScheduleAppointment = () => {
  const [isReschedulingError, setIsReschedulingError] = useState(false);
  const [isProcessingAppointmentUpdate, setIsProcessingAppointmentUpdate] =
    useState(false);
  const [hasSetAgentForReschedule, setHasSetAgentForReschedule] =
    useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [noAgentsAlertOpen, setNoAgentsAlertOpen] = useState(false);
  const [branchSearchTerm, setBranchSearchTerm] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');
  const [tagged, setTagged] = useState(false);
  const [noAdvisorsAvailableModalTagged, setNoAdvisorsAvailableModalTagged] =
    useState(false);
  const [noAdvisorsAvailableModalOpen, setNoAdvisorsAvailableModalOpen] =
    useState(false);

  const [
    hasAttemptedFallbackForVirtualFlow,
    setHasAttemptedFallbackForVirtualFlow,
  ] = useState(false);

  const hasTaggedNoAdvisorsModal = useRef(false);

  const featureFlags = useFeatureFlagsStore((state) => state.featureFlags);

  const {
    destinations,
    selectedDestination,
    searchedDestination,
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedAgent,
    shouldUseDefaultWorktype,
    selectedBranch,
    preSelectedZipCode,
    setSelectedAppointmentType,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedEndTime,
    setSelectedAgent,
    setSelectedBranch,
    setShouldUseDefaultWorktype,
    hasClosedNoAdvisorsAvailableModalForWorkTypeId,
    setHasClosedNoAdvisorsAvailableModalForWorkTypeId,
    contactInfo,
  } = useAppointmentStore();

  const { sessionId, sessionSource } = useSessionStore();

  const { isLuxuryTheme } = useThemeStore();

  const clubCode = useMemo(() => getClubCode(), []);

  const luxuryDestination = useMemo(
    () =>
      destinations.find(
        (destination) => destination.name?.toLowerCase() === 'luxury',
      ),
    [destinations],
  );

  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useLandingPageRedirection();

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const isRescheduleAppointment = useMemo(
    () => location.pathname.includes('reschedule-appointment'),
    [location.pathname],
  );

  const isInBranchAppointment = useAppointmentStore(
    isInBranchAppointmentSelected,
  );
  const isOnlineAppointment = useAppointmentStore(isOnlineAppointmentSelected);
  const validationTimeoutRef = useRef<number>();

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  const handleBranchSearchChange = useCallback((value: string) => {
    setBranchSearchTerm(value);
    const trimmed = value.trim();

    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    setZipCodeError('');

    if (!trimmed) {
      return;
    }

    // Only debounce validation for all-digit input
    if (!trimmed.match(/^\d+$/)) {
      return;
    }

    validationTimeoutRef.current = window.setTimeout(() => {
      if (!trimmed.match(/^\d{5}$/)) {
        setZipCodeError('Zip code must be 5 digits');
      }
    }, 500);
  }, []);

  const hasSelectedAgent = !!selectedAgent?.agentRecordId;
  const hasSelectedTime = !!selectedStartTime;
  const hasSelectedBranch = !!selectedBranch;
  const usaCanadaWorkTypeId =
    destinations?.find(
      (destination) =>
        destination.destinationApiName === 'United_States_&_Canada',
    )?.workTypeId || '';
  const otherDestinationWorkTypeId =
    destinations?.find((destination) => destination.name === 'Other')
      ?.workTypeId || '';

  const workTypeId = useMemo(() => {
    if (shouldUseDefaultWorktype && isLuxuryTheme) {
      return luxuryDestination?.workTypeId ?? '';
    }

    if (shouldUseDefaultWorktype && isInBranchAppointment) {
      return usaCanadaWorkTypeId;
    }

    if (shouldUseDefaultWorktype && isOnlineAppointment) {
      return otherDestinationWorkTypeId;
    }

    return selectedDestination.workTypeId;
  }, [
    shouldUseDefaultWorktype,
    isLuxuryTheme,
    luxuryDestination,
    isInBranchAppointment,
    usaCanadaWorkTypeId,
    selectedDestination,
    isOnlineAppointment,
    otherDestinationWorkTypeId,
  ]);

  const onlineAgentsAndTimeslotsQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId,
      inBranch: false,
      clubCode: clubCode,
    },
    {
      enabled:
        !!selectedDestination.workTypeId &&
        !(isRescheduleAppointment && isInBranchAppointment),
    },
  );

  const inBranchAgentsAndTimeslotsQuery = useAgentsAndTimeslotsQuery(
    {
      workTypeId,
      inBranch: true,
      branchId: selectedBranch?.branchId || '',
    },
    {
      enabled: !!selectedDestination.workTypeId && !!selectedBranch,
    },
  );

  const agentsAndTimeslotsQuery = isInBranchAppointment
    ? inBranchAgentsAndTimeslotsQuery
    : onlineAgentsAndTimeslotsQuery;

  const [agentsAndTimeSlots, setAgentsAndTimeSlots] = useState<AgentInfo[]>();

  const hasAvailableAgents = !!agentsAndTimeSlots?.length;
  const isInBranchDataLoading = inBranchAgentsAndTimeslotsQuery.isFetching;
  const isOnlineDataLoading = onlineAgentsAndTimeslotsQuery.isFetching;
  const isAgentsAndTimeslotsLoading = agentsAndTimeslotsQuery.isFetching;

  const agentData = agentsAndTimeslotsQuery.data;

  const zipCode = useMemo(
    () => preSelectedZipCode || getZipCode(),
    [preSelectedZipCode],
  );

  const baseZipCodeBranchesQuery = useBranches(
    { zipCode },
    {
      enabled: !isRescheduleAppointment,
    },
  );

  const trimmedBranchSearchTerm = branchSearchTerm.trim();
  const isOnlyDigits = !!trimmedBranchSearchTerm.match(/^\d+$/);
  const isFiveDigitZip = !!trimmedBranchSearchTerm.match(/^\d{5}$/);

  const shouldShowDefaultBranches =
    !trimmedBranchSearchTerm || (isOnlyDigits && !isFiveDigitZip);

  const userInputBranchesQuery = useBranches(
    {
      zipCode: isFiveDigitZip ? trimmedBranchSearchTerm : zipCode,
      name: isFiveDigitZip ? undefined : trimmedBranchSearchTerm,
    },
    {
      enabled:
        !!branchSearchTerm && !zipCodeError && !shouldShowDefaultBranches,
    },
  );

  const activeQuery = shouldShowDefaultBranches
    ? baseZipCodeBranchesQuery
    : userInputBranchesQuery;

  const branchesData = activeQuery.data?.length
    ? activeQuery.data.sort((a: Branch, b: Branch) => a.distance - b.distance)
    : [];

  const isSearchLoading =
    !shouldShowDefaultBranches &&
    !!branchSearchTerm &&
    !zipCodeError &&
    userInputBranchesQuery.isFetching;

  useEffect(() => {
    if (!agentData) {
      setAgentsAndTimeSlots(undefined);
      return;
    }

    // Simple shuffle for randomization
    const shuffledData = shuffleArray(agentData);
    setAgentsAndTimeSlots(shuffledData);
  }, [agentData]);

  useEffect(() => {
    if (!preSelectedZipCode) return;
    const preSelectedBranch = baseZipCodeBranchesQuery.data?.find(
      (branch: Branch) => branch.branchAddress.includes(preSelectedZipCode),
    );
    if (!preSelectedBranch) return;
    setSelectedBranch(preSelectedBranch);
  }, [preSelectedZipCode, baseZipCodeBranchesQuery.data, setSelectedBranch]);

  const tagging = useTagging();

  let flow: FlowType;
  if (isRescheduleAppointment) {
    flow = 'modify';
  } else {
    flow = '';
  }

  useEffect(() => {
    if (!tagged && agentsAndTimeSlots) {
      const trvEndLocation = searchedDestination || selectedDestination.name;

      tagging.tag('view', sessionSource, {
        page: Page.selectAppointment,
        sessionid: sessionId,
        flow,
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });

      setTagged(true);
    }
  }, [
    agentsAndTimeSlots,
    tagged,
    searchedDestination,
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    flow,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
  ]);

  const timeSlots: TimeSlot[] = useMemo(() => {
    if (!agentsAndTimeSlots || !agentsAndTimeSlots.length) {
      return [];
    }
    const allTimeSlots = agentsAndTimeSlots.flatMap((agent) =>
      agent.timeSlots.map((timeSlot) => ({
        startTime: timeSlot.startTime,
        endTime: timeSlot.endTime,
      })),
    );
    const uniqueTimeSlots = Array.from(
      new Set(allTimeSlots.map((a) => a.startTime)),
    ).map(
      (startTime) =>
        allTimeSlots.find((a) => a.startTime === startTime) as TimeSlot,
    );
    return uniqueTimeSlots.sort(
      (a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix(),
    );
  }, [agentsAndTimeSlots]);

  // Agents available for selected time slot with selected agent first
  useEffect(() => {
    if (!selectedAgent.agentRecordId || !agentsAndTimeSlots) return;

    const selectedAgentIndex = agentsAndTimeSlots.findIndex(
      (agent) => agent.agentRecordId === selectedAgent.agentRecordId,
    );
    if (selectedAgentIndex > 0) {
      const copy = [...agentsAndTimeSlots];
      const [selected] = copy.splice(selectedAgentIndex, 1);
      copy.unshift(selected);
      setAgentsAndTimeSlots(copy);
    }
  }, [selectedAgent, agentsAndTimeSlots]);

  const matchingAgents = useMemo(() => {
    if (
      !agentsAndTimeSlots?.length ||
      !selectedDate ||
      !selectedStartTime ||
      !selectedEndTime
    ) {
      return [];
    }

    return agentsAndTimeSlots.filter((agent) =>
      agent.timeSlots.some(
        (timeSlot) =>
          timeSlot.startTime === selectedStartTime &&
          timeSlot.endTime === selectedEndTime,
      ),
    );
  }, [agentsAndTimeSlots, selectedDate, selectedStartTime, selectedEndTime]);

  useEffect(() => {
    if (agentData && !agentData.length) {
      setSelectedDate(undefined);
      setSelectedStartTime('');
      setSelectedEndTime('');
    } else if (!selectedDate && timeSlots.length) {
      setSelectedDate(new Date(timeSlots[0]?.startTime));
      setSelectedStartTime(timeSlots[0]?.startTime);
      setSelectedEndTime(timeSlots[0]?.endTime);
    }
  }, [
    agentData,
    selectedDate,
    timeSlots,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedEndTime,
  ]);

  useEffect(() => {
    if (
      isRescheduleAppointment &&
      agentsAndTimeSlots?.length &&
      selectedAgent.agentRecordId &&
      !hasSetAgentForReschedule
    ) {
      const currentAgent = agentsAndTimeSlots.find(
        (agent) => agent.agentRecordId === selectedAgent.agentRecordId,
      );
      if (!currentAgent) {
        setSelectedAgent(agentsAndTimeSlots[0]);
      } else {
        setSelectedDate(new Date(currentAgent.timeSlots[0].startTime));
        setSelectedStartTime(currentAgent.timeSlots[0].startTime);
        setSelectedEndTime(currentAgent.timeSlots[0].endTime);
      }
      setHasSetAgentForReschedule(true);
    }
  }, [
    isRescheduleAppointment,
    agentsAndTimeSlots,
    selectedAgent,
    setSelectedAgent,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedEndTime,
    hasSetAgentForReschedule,
  ]);

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      setSelectedDate(date);
      if (date && timeSlots.length) {
        const firstSlot = timeSlots.find((slot) =>
          dayjs(slot.startTime).isSame(date, 'day'),
        );
        if (firstSlot) {
          setSelectedStartTime(firstSlot.startTime);
          setSelectedEndTime(firstSlot.endTime);
        } else {
          setSelectedStartTime('');
          setSelectedEndTime('');
        }
      } else {
        setSelectedStartTime('');
        setSelectedEndTime('');
      }
    },
    [setSelectedDate, setSelectedStartTime, setSelectedEndTime, timeSlots],
  );

  useEffect(() => {
    if (isRescheduleAppointment && !hasSetAgentForReschedule) return;
    if (selectedStartTime && matchingAgents.length > 0) {
      const isCurrentAgentAvailable = matchingAgents.some(
        (agent) => agent.agentRecordId === selectedAgent.agentRecordId,
      );
      if (!isCurrentAgentAvailable) {
        setSelectedAgent(matchingAgents[0]);
      }
    } else if (selectedStartTime && timeSlots.length) {
      handleDateChange(new Date(timeSlots[0]?.startTime));
    }
  }, [
    setSelectedAgent,
    matchingAgents,
    selectedAgent,
    selectedStartTime,
    timeSlots,
    isRescheduleAppointment,
    hasSetAgentForReschedule,
    handleDateChange,
  ]);

  const handleProcessingAppointmentUpdate = useCallback(() => {
    setIsProcessingAppointmentUpdate(true);
  }, []);

  const handleInvalidTime = useCallback(
    (currentAgent?: AgentInfo) => {
      handleDateChange(
        new Date(
          currentAgent?.timeSlots[0]?.startTime ||
            matchingAgents[0].timeSlots[0].startTime,
        ),
      );
    },
    [handleDateChange, matchingAgents],
  );

  const handleAppointmentUpdateError = useCallback(() => {
    setIsReschedulingError(true);
    setIsProcessingAppointmentUpdate(false);
  }, []);

  const { isValidTime, isValidatingTimeSlot, validateSelectedTime } =
    useValidateSelectedTime({
      isRescheduleAppointment,
      refetch: agentsAndTimeslotsQuery.refetch,
      onValidForReschedule: handleProcessingAppointmentUpdate,
      onInvalidTime: handleInvalidTime,
      onUpdateAppointmentError: handleAppointmentUpdateError,
    });

  const handleClickContinue = useCallback(() => {
    validateSelectedTime();
  }, [validateSelectedTime]);

  useEffect(() => {
    const noTimeSlotsForLuxuryFlow =
      isLuxuryTheme && agentsAndTimeSlots && !agentsAndTimeSlots.length;
    const isInBranchWithoutAgents =
      isInBranchAppointment && agentsAndTimeSlots && !agentsAndTimeSlots.length;
    const noTimeSlotsForVirtualFlow =
      isOnlineAppointment && agentsAndTimeSlots && !agentsAndTimeSlots.length;

    if (
      noTimeSlotsForLuxuryFlow ||
      isInBranchWithoutAgents ||
      noTimeSlotsForVirtualFlow
    ) {
      setShouldUseDefaultWorktype(true);
    }
  }, [
    isLuxuryTheme,
    agentsAndTimeSlots,
    isInBranchAppointment,
    isOnlineAppointment,
    setShouldUseDefaultWorktype,
  ]);

  useEffect(() => {
    if (agentsAndTimeSlots && !isValidTime) {
      setAlertOpen(true);
    }
  }, [agentsAndTimeSlots, isValidTime]);

  useEffect(() => {
    if (
      hasClosedNoAdvisorsAvailableModalForWorkTypeId &&
      hasClosedNoAdvisorsAvailableModalForWorkTypeId !==
        selectedDestination.workTypeId
    ) {
      setHasClosedNoAdvisorsAvailableModalForWorkTypeId(undefined);
    }
  }, [
    selectedDestination.workTypeId,
    hasClosedNoAdvisorsAvailableModalForWorkTypeId,
    setHasClosedNoAdvisorsAvailableModalForWorkTypeId,
  ]);

  const modalTagging = useCallback(
    (page: string) => {
      if (
        page === Page.agentsUnavailableModal &&
        noAdvisorsAvailableModalTagged
      ) {
        return;
      }

      if (page === Page.agentsUnavailableModal) {
        setNoAdvisorsAvailableModalTagged(true);
      }

      tagging.tag('view', sessionSource, {
        page,
        sessionid: sessionId,
        flow,
        trv_end_location: searchedDestination || selectedDestination.name,
        comm_pref: page === Page.agentsUnavailableModal ? 'phone' : undefined,
      });
    },
    [
      sessionSource,
      sessionId,
      flow,
      searchedDestination,
      selectedDestination.name,
      tagging.tag,
      noAdvisorsAvailableModalTagged,
    ],
  );

  useEffect(() => {
    if (
      isInBranchAppointment &&
      selectedBranch &&
      !inBranchAgentsAndTimeslotsQuery.isFetching
    ) {
      if (inBranchAgentsAndTimeslotsQuery.data?.length) {
        setNoAgentsAlertOpen(false);
      } else {
        setNoAgentsAlertOpen(true);
      }
    } else {
      setNoAgentsAlertOpen(false);
    }
  }, [
    isInBranchAppointment,
    selectedBranch,
    inBranchAgentsAndTimeslotsQuery.isFetching,
    inBranchAgentsAndTimeslotsQuery.data,
  ]);

  useEffect(() => {
    if (!featureFlags.inBranchAppointments) {
      setNoAdvisorsAvailableModalOpen(false);
      return;
    }

    if (isOnlineAppointment && !isOnlineDataLoading) {
      if (agentData?.length) {
        setNoAdvisorsAvailableModalOpen(false);
        setHasAttemptedFallbackForVirtualFlow(false);
      } else {
        const noAgentsAvailable = !agentData?.length;
        const shouldShowModal =
          hasClosedNoAdvisorsAvailableModalForWorkTypeId !==
          selectedDestination.workTypeId;

        const effectiveWorkTypeId = shouldUseDefaultWorktype
          ? otherDestinationWorkTypeId
          : selectedDestination.workTypeId;

        const isFallbackActive =
          (shouldUseDefaultWorktype &&
            effectiveWorkTypeId !== selectedDestination.workTypeId) ||
          (effectiveWorkTypeId === otherDestinationWorkTypeId &&
            noAgentsAvailable);

        if (
          noAgentsAvailable &&
          hasAttemptedFallbackForVirtualFlow &&
          shouldShowModal &&
          isFallbackActive &&
          !agentsAndTimeSlots?.length
        ) {
          setNoAdvisorsAvailableModalOpen(true);
          if (!hasTaggedNoAdvisorsModal.current) {
            modalTagging(Page.agentsUnavailableModal);
            hasTaggedNoAdvisorsModal.current = true;
          }
        }
        if (!hasAttemptedFallbackForVirtualFlow) {
          setHasAttemptedFallbackForVirtualFlow(true);
        }
      }
    } else {
      setNoAdvisorsAvailableModalOpen(false);
      setHasAttemptedFallbackForVirtualFlow(false);
    }
  }, [
    isOnlineAppointment,
    isOnlineDataLoading,
    agentData,
    featureFlags.inBranchAppointments,
    hasClosedNoAdvisorsAvailableModalForWorkTypeId,
    selectedDestination.workTypeId,
    agentsAndTimeSlots?.length,
    otherDestinationWorkTypeId,
    selectedDestination,
    modalTagging,
    hasAttemptedFallbackForVirtualFlow,
    shouldUseDefaultWorktype,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    hasTaggedNoAdvisorsModal.current = false;
  }, [selectedDestination]);

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const handleCloseNoAgentsAlert = () => {
    setNoAgentsAlertOpen(false);
  };

  const handleCloseNoAdvisorsAvailableModal = () => {
    setNoAdvisorsAvailableModalOpen(false);
    setHasClosedNoAdvisorsAvailableModalForWorkTypeId(
      selectedDestination.workTypeId,
    );
  };

  const handleContinueNoAdvisorsAvailable = () => {
    setSelectedAppointmentType('in-branch');
    setNoAdvisorsAvailableModalOpen(false);
    setHasClosedNoAdvisorsAvailableModalForWorkTypeId(
      selectedDestination.workTypeId,
    );
  };

  if (isReschedulingError) {
    return <ErrorPage onClickTryAgain={() => navigate(redirectTo)} />;
  }

  if (isValidatingTimeSlot || isProcessingAppointmentUpdate) {
    const radiosInfo = [
      {
        radioMessage: 'Verifying advisor availability',
        isChecked: true,
      },
    ];

    if (isRescheduleAppointment) {
      return (
        <LoadingContainer
          message='Confirming your new appointment date and time...'
          radiosInfo={radiosInfo}
        />
      );
    }
    return (
      <LoadingContainer
        message='Confirming your appointment date and time...'
        radiosInfo={radiosInfo}
      />
    );
  }

  if (agentsAndTimeslotsQuery.isError) {
    return <ErrorPage onClickTryAgain={agentsAndTimeslotsQuery.refetch} />;
  }

  if (
    !featureFlags.inBranchAppointments &&
    isOnlineAppointment &&
    agentsAndTimeslotsQuery.isSuccess &&
    !agentsAndTimeslotsQuery.data?.length
  ) {
    return (
      <NoAgentsFound
        onClickTryAgain={() => {
          navigate('/', { replace: true });
        }}
      />
    );
  }

  const noAgentsAvailableAlert = (
    <Box sx={{ alignSelf: 'stretch', px: 2, mt: 2 }}>
      <Alert
        visible={true}
        severity={'error'}
        data-testid='noAgentsAvailableAlert'
        onClose={handleCloseNoAgentsAlert}
        sx={{
          '&.MuiPaper-root': {
            maxWidth: 'none !important',
            textAlign: 'left',
          },
        }}
      >
        There are currently no available advisors. Please select a different
        branch.
      </Alert>
    </Box>
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Backdrop
        open={alertOpen}
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
        })}
        onClick={handleCloseAlert}
      >
        <Alert
          visible={true}
          severity={'error'}
          sx={[
            {
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translate(-50%, -0%)',
              width: '80%',
            },
            isLuxuryTheme && {
              '& .MuiAlert-Typography-root': {
                color: 'secondary.300',
              },
              '& .MuiIconButton-root.MuiAlertCloseIconButton': {
                color: 'secondary.300',
              },
            },
          ]}
          data-testid='invalidTimeSlotAlert'
          onClose={handleCloseAlert}
        >
          That date & time has already been booked. Please select a different
          time.
        </Alert>
      </Backdrop>
      <Box
        data-testid='scheduleAppointmentContainer'
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
        }}
      >
        {isMobileView ? (
          <MobileWizard
            steps={[
              {
                content: (
                  <Stack
                    key='appointmentSelection'
                    direction='column'
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <AppointmentTypeModule
                      branchSearchTerm={branchSearchTerm}
                      setBranchSearchTerm={handleBranchSearchChange}
                      branchesData={branchesData}
                      isLoading={isSearchLoading}
                      zipCodeError={zipCodeError}
                    />
                    <NoAdvisorsAvailableModal
                      open={noAdvisorsAvailableModalOpen}
                      onClose={handleCloseNoAdvisorsAvailableModal}
                      onContinue={handleContinueNoAdvisorsAvailable}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <BranchSelectionModule
                        setBranchSearchTerm={handleBranchSearchChange}
                        branchesData={branchesData}
                        isLoading={isSearchLoading}
                      />
                      {noAgentsAlertOpen && noAgentsAvailableAlert}
                      <DateSelectionModule
                        header='Select date & time'
                        timeSlots={timeSlots}
                        disabled={
                          (isInBranchAppointment && !selectedBranch) ||
                          agentsAndTimeslotsQuery.isFetching
                        }
                        isLoading={agentsAndTimeslotsQuery.isFetching}
                        visible={
                          !noAgentsAlertOpen &&
                          (agentsAndTimeslotsQuery.isFetching ||
                            !!agentData?.length)
                        }
                      />
                    </Box>
                  </Stack>
                ),
                disableNext:
                  (isInBranchAppointment && !selectedBranch) ||
                  !hasAvailableAgents ||
                  agentsAndTimeslotsQuery.isFetching,
              },
              {
                content: (
                  <Stack
                    key='agentSelection'
                    direction='column'
                    sx={{
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <AgentSelectionModule
                        matchingAgents={matchingAgents}
                        isLoading={agentsAndTimeslotsQuery.isFetching}
                      />
                    </Box>
                  </Stack>
                ),
              },
            ]}
            onClickContinue={handleClickContinue}
          />
        ) : (
          <SplitPageLayout
            blockRight={
              (isInBranchAppointment &&
                (!hasSelectedBranch ||
                  isInBranchDataLoading ||
                  !hasAvailableAgents)) ||
              !hasSelectedTime ||
              !hasSelectedAgent
            }
            leftSide={
              <Stack
                direction='column'
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <AppointmentTypeModule
                  branchSearchTerm={branchSearchTerm}
                  setBranchSearchTerm={handleBranchSearchChange}
                  branchesData={branchesData}
                  isLoading={isSearchLoading}
                  zipCodeError={zipCodeError}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <BranchSelectionModule
                    setBranchSearchTerm={handleBranchSearchChange}
                    branchesData={branchesData}
                    isLoading={isSearchLoading}
                  />

                  {noAgentsAlertOpen && noAgentsAvailableAlert}
                  <AgentSelectionModule
                    matchingAgents={matchingAgents}
                    isLoading={agentsAndTimeslotsQuery.isFetching}
                  />
                </Box>
              </Stack>
            }
            rightSide={
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  justifyContent: 'space-between',
                  width: '100%',
                  mt: {
                    xs: 2,
                    lg: 12,
                  },
                }}
              >
                <NoAdvisorsAvailableModal
                  open={noAdvisorsAvailableModalOpen}
                  onClose={handleCloseNoAdvisorsAvailableModal}
                  onContinue={handleContinueNoAdvisorsAvailable}
                />
                <DateSelectionModule
                  header='Select date & time to speak with an advisor'
                  subHeader='Book an appointment'
                  timeSlots={timeSlots}
                  disabled={
                    (isInBranchAppointment && !selectedBranch) ||
                    agentsAndTimeslotsQuery.isFetching
                  }
                  isLoading={agentsAndTimeslotsQuery.isFetching}
                />
              </Box>
            }
            progressBarProps={{
              buttonDisabled:
                isAgentsAndTimeslotsLoading ||
                isOnlineDataLoading ||
                !hasSelectedAgent ||
                !hasSelectedTime ||
                (isInBranchAppointment &&
                  (!hasSelectedBranch ||
                    !hasAvailableAgents ||
                    isInBranchDataLoading)),
              displayBackButton: true,
              onClickContinue: handleClickContinue,
            }}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default ScheduleAppointment;
